/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Campaign Info Action types
 *
 * @author Yakov Gushchin
 * @version 1.12.0
 * @since 1.12.0
 */

import { IAction, IActionWithPayload } from '../../../modules/app/createAction';
import { IRouteParams } from '../../../routes/constants/routesConstants';
import {
    CAMPAIGN_INFO_ACTIVATE_START,
    CAMPAIGN_INFO_ACTIVATE_SUCCESS,
    CAMPAIGN_INFO_GET_ACTIVATE_TYPE,
    CAMPAIGN_INFO_INIT_FAILURE,
    CAMPAIGN_INFO_INIT_START,
    CAMPAIGN_INFO_INIT_SUCCESS,
    CAMPAIGN_INFO_LINK_VISITED,
    CAMPAIGN_INFO_REMOVE,
} from '../campaignInfoConstant';
import { ECampaignInfoActions, ICampaignInfoActivateResult, ICampaignInfoGeneral } from './campaignInfoTypes';


/*
* -------------------------------------------
*            INIT PAGE
* -------------------------------------------
*/

export type TCampaignInfoInitStartPayload = Pick<IRouteParams, 'campaignId'>;

export type TCampaignInfoInitStartAction =
    IActionWithPayload<typeof CAMPAIGN_INFO_INIT_START, TCampaignInfoInitStartPayload>;

export type TCampaignInfoInitSuccessPayload = ICampaignInfoGeneral & Pick<
    ICampaignInfoInitSuccessResponse, 
    'actions' |
    'maxParticipantsForActivation'
    >;

export interface ICampaignInfoInitSuccessResponse {
    actions: ECampaignInfoActions[];
    buttonText: string;
    description?: string;
    id: number;
    imgUrl?: string;
    pageUrl?: string;
    type: 'CLIENT' | 'SUBSCRIBER';
    maxParticipantsForActivation?: number;
}

export type TCampaignInfoInitSuccessAction =
    IActionWithPayload<typeof CAMPAIGN_INFO_INIT_SUCCESS, TCampaignInfoInitSuccessPayload>;

export type TCampaignInfoInitFailureAction = IAction<typeof CAMPAIGN_INFO_INIT_FAILURE>;

export type TCampaignInfoLinkVisitedAction =
    IActionWithPayload<typeof CAMPAIGN_INFO_LINK_VISITED, TCampaignInfoInitStartPayload>;

/*
* -------------------------------------------
*            MODAL APPLY & REMOVE
* -------------------------------------------
*/

export enum ECampaignInfoRedirectType {
    service = 'service',
    tariff = 'ratePlans',
}

export enum ECampaignInfoActivateType {
    inquiry = 'INQUIRY',
    ratePlan = 'RATE_PLAN',
}

/**
 * Приоритеты:
 *  1) Проверка на редирект
 *  2) Проверка на создание обращения
 *  3) Если не было редиректа, проверка на оставшиеся кол-во записей в таблице
 */
export interface ICampaignInfoActivateResponse {
    // Если пришел id, то генерируем стандартную модальное окно с номером обращения
    campaignInquiryId?: number;
    // Если пришел, значит нужно делать редирект на мастер тарифов или услуг
    redirect?: {
        id: string;
        type: ECampaignInfoRedirectType;
    };
    // Если remain > 0, то нужно перезапросить фильтр таблицы
    remain?: number;
}

export interface ICampaignInfoActivateTypeResponse {
    campaignActivationType: ECampaignInfoActivateType;
}

export type TCampaignInfoRemoveResponse = Pick<ICampaignInfoActivateResponse, 'remain'>;

// Activate
export type TCampaignInfoGetActivateTypeAction = IAction<typeof CAMPAIGN_INFO_GET_ACTIVATE_TYPE>;

export type TCampaignInfoActivateStartAction = IAction<typeof CAMPAIGN_INFO_ACTIVATE_START>;

export type TCampaignInfoActivateSuccessPayload = ICampaignInfoActivateResult;
export type TCampaignInfoActivateSuccessAction =
    IActionWithPayload<typeof CAMPAIGN_INFO_ACTIVATE_SUCCESS, TCampaignInfoActivateSuccessPayload>;

// Remove

export type TCampaignInfoRemoveAction = IAction<typeof CAMPAIGN_INFO_REMOVE>;

/*
* -------------------------------------------
*           All types for reducer
* -------------------------------------------
*/

export type TPageCampaignInfoActions =
    | TCampaignInfoActivateStartAction
    | TCampaignInfoActivateSuccessAction
    | TCampaignInfoInitFailureAction
    | TCampaignInfoInitStartAction
    | TCampaignInfoInitSuccessAction
    | TCampaignInfoLinkVisitedAction
    | TCampaignInfoRemoveAction
    ;
